import React, { useEffect, useState } from "react";
import clsx from "clsx";
import moment from "moment";
import { Box, Typography } from "@material-ui/core";
import {
  CustomCard,
  CustomMenu,
  GetFileIconbyDocumentName,
} from "../../../../../../../Components";
import { useIsAr, useSelectedTheme } from "../../../../../../../Hooks";
import { getDownloadableLink } from "../../../../../../../Helper";
import { ContactPreviewDocumentsDialog } from "../../../../../NewContactsView/ContactOverview/UI";

//Icons
import {
  MoreVertIcon,
  Image01Icon,
  AttatchmentIcon,
  FileIcon,
  ErrorImageIcon,
  EyeIcon,
} from "../../../../../../../assets/icons";

// styles
import useStyles from "./styles";

function formatDocumentName(documentName) {
  if (!documentName) return "Untitled";
  const namePart = documentName.split(".")[0].slice(0, 13);
  const extension = documentName.split(".")[1];
  return `${namePart}${extension ? "." + extension : ""}`;
}

function formatCreatorName(creator, isAr) {
  return creator
    ? isAr
      ? `${creator.slice(-9)}...`
      : `${creator.slice(0, 9)}...`
    : "Unknown";
}

const DocumentsUnitCard = ({ documentItem }) => {
  const styles = useStyles();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { isAr } = useIsAr();
  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [isPreviewDailogOpen, setIsPreviewDialogOpen] = useState(false);

  const formattedDate = documentItem?.createdOn
    ? moment(documentItem?.createdOn).locale("en").format("D MMM YYYY")
    : "Date Unknown";

  const documentName = formatDocumentName(
    documentItem?.fileName || documentItem?.documentName
  );
  const creatorName = formatCreatorName(documentItem?.createdBy, isAr);
  const ID = documentItem?.documentId || documentItem?.fileId;

  useEffect(() => {
    if (
      ID &&
      ["png", "jpg", "jpeg", "gif", "bmp", "tiff", "webp", "svg"].includes(
        documentName.split(".").pop().toLowerCase()
      )
    ) {
      const url = getDownloadableLink(ID);
      setImageUrl(url);
    }
  }, [ID, documentName]);

  const onPreviewClickHandler = () => {
    setIsPreviewDialogOpen(true);
  };

  return (
    <>
      <CustomCard
        borderRadius="xl"
        borderColor="secondary"
        classes={styles.cardWrapper}
      >
        <Box className={styles.cardContainer}>
          <Box
            className={clsx(styles.headingContainer, {
              [styles.withUrl]: documentItem?.documentLink,
              [styles.withImage]:
                ID &&
                [
                  "png",
                  "jpg",
                  "jpeg",
                  "gif",
                  "bmp",
                  "tiff",
                  "webp",
                  "svg",
                ].includes(documentName.split(".").pop().toLowerCase()),
              [styles.withDocument]:
                documentName &&
                ![
                  "png",
                  "jpg",
                  "jpeg",
                  "gif",
                  "bmp",
                  "tiff",
                  "webp",
                  "svg",
                ].includes(documentName.split(".").pop().toLowerCase()),
            })}
          >
            <Box className={styles.imageWrapper}>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={documentItem.fileName || documentItem.documentName}
                  className={styles.imagePreview}
                  onError={(e) => {
                    e.target.onerror = null;
                    setImageError(true);
                  }}
                />
              ) : imageError ? (
                <ErrorImageIcon
                  width="28"
                  height="28"
                  fill={palette.border.tertiary}
                  className={styles.errorIcon}
                />
              ) : documentItem?.documentLink ? (
                <Box className={styles.containerAttatchmentIcon}>
                  <AttatchmentIcon
                    width="28"
                    height="28"
                    fill={palette.border.tertiary}
                  />
                </Box>
              ) : documentName ? (
                <>
                  {GetFileIconbyDocumentName({
                    documentName: documentName
                      ?.split(".")
                      ?.pop()
                      ?.toLowerCase(),
                    height: "56",
                    width: "56",
                  })}
                </>
              ) : (
                <span className={styles.noImageText}>
                  There is no preview available
                </span>
              )}
            </Box>
            <CustomMenu
              menuItems={[
                ID && {
                  text: "Preview",
                  icon: (
                    <EyeIcon
                      width="16"
                      height="16"
                      fill={palette.foreground.quarterary}
                    />
                  ),
                  handler: () => {
                    onPreviewClickHandler(documentItem);
                  },
                },
              ].filter(Boolean)}
              activeIconClass={styles.menuActiveIconClass}
              defaultIconClass={styles.menuDefaultIconClass}
            >
              <MoreVertIcon width="20" height="20" />
            </CustomMenu>
          </Box>

          <Box className={styles.contentContainer}>
            <Box className={styles.titleWrapper}>
              <Typography
                className={styles.categoryName}
                variant="h6"
                align="center"
              >
                {documentItem?.categoryName || "No Category"}
              </Typography>
              <Box className={styles.documentNameWrapper}>
                {documentItem.fileName || documentItem.documentName ? (
                  [
                    "png",
                    "jpg",
                    "jpeg",
                    "gif",
                    "bmp",
                    "tiff",
                    "webp",
                    "svg",
                  ].includes(documentName.split(".").pop().toLowerCase()) ? (
                    <>
                      <Image01Icon
                        width="20"
                        height="20"
                        fill={palette.utility.utility_gray_400}
                      />
                      <span className={styles.documentName}>
                        {documentName}
                      </span>
                    </>
                  ) : (
                    <>
                      <FileIcon
                        width="20"
                        height="20"
                        fill={palette.utility.utility_gray_400}
                      />
                      <span className={styles.documentLink}>
                        {documentItem.documentName || documentItem.fileName}
                      </span>
                    </>
                  )
                ) : documentItem?.documentLink ? (
                  <Box
                    className={styles.documentLinkWrapper}
                    onClick={() =>
                      window.open(documentItem.documentLink, "_blank")
                    }
                  >
                    <AttatchmentIcon
                      width="20"
                      height="20"
                      fill={palette.utility.utility_gray_400}
                    />
                    <span className={styles.documentLink}>
                      {documentItem.documentLink}
                    </span>
                  </Box>
                ) : null}
              </Box>
            </Box>
            <Box className={styles.footerContainer}>
              <Typography
                className={styles.createdOn}
                variant="body1"
                align="center"
              >
                Created On: <b className={styles.date}>{formattedDate}</b>{" "}
                <span>{creatorName}</span>
              </Typography>
            </Box>
          </Box>
        </Box>
      </CustomCard>
      <ContactPreviewDocumentsDialog
        OnOpen={isPreviewDailogOpen}
        onClose={() => {
          setIsPreviewDialogOpen(false);
        }}
        documentItem={documentItem}
      />
    </>
  );
};

export default DocumentsUnitCard;
